<template>
    <div :class="disabled ? 'cursor-not-allowed opacity-50' : ''" class="flex items-start gap-3 rounded-lg border border-gray-200 px-4 pt-2 pb-3">
        <div class="flex flex-1 flex-col gap-3 sm:flex-row">
            <jet-select
                v-model="areaId"
                :disabled="disabled || isLoading || areaOptions.length === 0"
                :enable-search="true"
                :horizontal="false"
                :is-loading="isLoading"
                :label="trans('assets.details.area_location.label')"
                :options="areaOptions"
                class="flex-1"
                form-group-classes="mb-0"
                name="areaId"
                @update:model-value="handleAreaChange($event)"
            >
                <template v-slot:item-label="{ option }">
                    <span>{{ option.label }}</span>
                </template>
            </jet-select>
            <jet-select
                v-model="subAreaId"
                :disabled="disabled || isLoading || subAreaOptions.length === 0"
                :enable-search="true"
                :horizontal="false"
                :is-loading="isLoading"
                :label="trans('assets.details.sub_area_location.label')"
                :options="subAreaOptions"
                class="flex-1"
                form-group-classes="mb-0"
                name="subAreaId"
                @update:model-value="handleSubAreaChange($event)"
            >
                <template v-slot:item-label="{ option }">
                    <span>{{ option.label }}</span>
                </template>
            </jet-select>
        </div>
        <div class="flex shrink-0 flex-col items-center">
            <!-- Invisible spacer matching JetSelect's label box so the checkmark aligns with the inputs. -->
            <FormLabel aria-hidden="true" class="invisible">&nbsp;</FormLabel>
            <button
                :class="{ 'cursor-pointer': !disabled }"
                :aria-label="trans('general.action.confirm')"
                :aria-pressed="isConfirmed"
                :disabled="disabled"
                class="flex h-10 items-center"
                type="button"
                @click.stop="toggleConfirm"
            >
                <check-circle-icon :active="isConfirmed" class="h-10 w-10" />
            </button>
        </div>
    </div>
</template>
<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import JetSelect from '@/Jetstream/Select.vue';
import FormLabel from '@/Components/FormLabel.vue';
import CheckCircleIcon from '@/Components/Icons/CheckCircleIcon.vue';
import { trans } from '@/mixins/i18n';
import { $http } from '@/bootstrap.js';

const props = defineProps({
    disabled: Boolean,
    locationTree: {
        type: Object,
        required: true,
    },
    value: { type: String, default: null },
});

const emit = defineEmits(['change']);

const toOptions = (locations) =>
    (locations ?? []).map((loc) => ({
        label: loc.name,
        value: loc.id,
        searchableContent: loc.name,
    }));

const ancestors = props.locationTree.ancestorsAndSelfLocationIds ?? [];
// Fallback when the operator leaves Area/Subarea empty — both are optional.
const specificLocationId = ancestors[1] ?? ancestors[0] ?? null;
const initialAreaId = ancestors[2] ?? null;
const initialSubAreaId = ancestors[3] ?? null;

const areaId = ref(initialAreaId);
const subAreaId = ref(initialSubAreaId);
const areaOptions = ref(toOptions(props.locationTree.areaLocations?.data));
const subAreaOptions = ref(toOptions(props.locationTree.subAreaLocations?.data));
const isLoading = ref(false);

const isConfirmed = computed(() => props.value !== null && props.value !== undefined);

const deepestSelectedId = computed(() => subAreaId.value || areaId.value || specificLocationId);

const unconfirm = () => {
    if (isConfirmed.value) {
        emit('change', null);
    }
};

// Cancel in-flight lookups on unmount so response handlers don't hit a disposed component.
const abortController = new AbortController();

const loadChildren = (parentId) => {
    isLoading.value = true;

    return $http.get(route('locations.children', parentId), { signal: abortController.signal }).finally(() => {
        isLoading.value = false;
    });
};

const handleAreaChange = (id) => {
    areaId.value = id;
    subAreaId.value = null;
    subAreaOptions.value = [];
    unconfirm();

    if (!id) return;

    loadChildren(id).then((resp) => {
        subAreaOptions.value = toOptions(resp?.data?.children);
    });
};

// getLocationTree only preloads one level past the asset's depth, so assets at specific-location
// level arrive with empty areas/subareas. Fetch them here so the dropdowns aren't locked.
onMounted(() => {
    if (areaOptions.value.length === 0 && specificLocationId) {
        loadChildren(specificLocationId).then((resp) => {
            areaOptions.value = toOptions(resp?.data?.children);
        });
    }

    if (subAreaOptions.value.length === 0 && areaId.value) {
        loadChildren(areaId.value).then((resp) => {
            subAreaOptions.value = toOptions(resp?.data?.children);
        });
    }
});

onBeforeUnmount(() => {
    abortController.abort();
});

const handleSubAreaChange = (id) => {
    subAreaId.value = id;
    unconfirm();
};

const toggleConfirm = () => {
    if (props.disabled) return;

    if (isConfirmed.value) {
        emit('change', null);
        return;
    }

    const id = deepestSelectedId.value;
    if (!id) return;

    emit('change', id);
};
</script>

<style scoped>
/* Tighten the default py-3 — a bit airy for a row-among-rows in the testing-sequence modal. */
:deep([data-testid^='select-']) {
    padding-top: 0.5rem !important;
    padding-bottom: 0.5rem !important;
}
</style>
